import { useMonitoringStore } from '@/features/monitoring/store/useMonitoringStore';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

interface FilterFormValues {
  month: string;
  action_id: string;
  tax_number?: string;
}

const MONTH_NAMES = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
];

const getCurrentMonth = (): string => MONTH_NAMES[new Date().getMonth()];

export const useMasterApprenticesFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchActionList, actionListData } = useMonitoringStore();

  const defaultMonth = searchParams.get('month') || getCurrentMonth();

  const form = useForm<FilterFormValues>({
    defaultValues: {
      month: defaultMonth,
      action_id: searchParams.get('action_id') || '',
      tax_number: '',
    },
  });

  const monthValue = form.watch('month');

  useEffect(() => {
    if (!searchParams.get('month')) {
      setSearchParams(
        (prev) => {
          prev.set('month', defaultMonth);
          return prev;
        },
        { replace: true },
      );
    }
  }, []);

  useEffect(() => {
    if (monthValue && monthValue !== 'all' && searchParams.get('region_id')) {
      fetchActionList({
        plant_id: '1',
        region_id: '',
        month: monthValue,
        year: '2026',
      });
    }
  }, [monthValue]);

  const actionOptions = useMemo(
    () => actionListData.map((item) => ({ value: item.id, label: item.name })),
    [actionListData],
  );

  const handleSearch = (values: FilterFormValues) => {
    setSearchParams((prev) => {
      if (values.month && values.month !== 'all') {
        prev.set('month', values.month);
      } else {
        prev.delete('month');
      }

      if (values.action_id) {
        prev.set('action_id', values.action_id);
      } else {
        prev.delete('action_id');
      }

      return prev;
    });
  };

  const handleClear = () => {
    const currentMonth = getCurrentMonth();
    form.reset({ month: currentMonth, action_id: '', tax_number: '' });
    setSearchParams((prev) => {
      prev.set('month', currentMonth);
      prev.delete('action_id');
      return prev;
    });
  };

  return {
    form,
    actionOptions,
    handleSearch,
    handleClear,
  };
};
